/**
 * The decisions a condition panel makes on a SINGLE ROW: which comparisons a
 * field allows, which values it offers, what happens to a row when its field
 * is swapped, whether a row is finished, and joining or removing rows.
 *
 * Moved here out of editor-v2/utils/conditionPanelModel.ts so both halves of
 * this codebase can make the SAME decisions: editor-v2 (Vue 3 / vitest)
 * imports it relatively, the Vue-2 project settings (Jest, which cannot
 * resolve `.ts` at all) import it normally -- the same reason
 * conditionFields.js, conditionSentence.js and contextRuleHelpers.js live
 * here.
 *
 * What did NOT move is everything that judges a WHOLE RULE:
 * `hasIncompleteRow` and `hasUnwritableMix` stay in editor-v2. They are about
 * the element rule's stored shape -- its `source`, and whether a row this
 * model cannot read can still be written back. A segment stores its `cases`
 * and nothing else: it has no source to mix, so the refusal has no meaning
 * there.
 *
 * Plain JavaScript, no Vue, no dependency beyond the shared modules next
 * door. A row is `{ id, path, operator, value?, kind, fieldType, segmentId?,
 * raw?, original? }` and a rule is `{ action, groups, raw?, event? }`; see
 * ConditionRow / ConditionRule in editor-v2/utils/conditionModel.ts. Type
 * declarations for TypeScript callers live in conditionRows.d.ts -- runtime
 * changes here MUST be mirrored there.
 */
import { OPERATORS, operatorsFor, isBooleanFieldType } from './contextRuleHelpers';
import { operatorWords } from './conditionSentence';

// The German words for the panel's own dropdown are the SAME words the
// sentence uses, so what an editor picks is what they then read back. That
// used to be a second copy of the list sitting in conditionPanelModel.ts with
// a comment promising it matched; now it is the one list, taken from the
// module that reads the condition out loud.
// `language` everywhere below: 'de' (default) or 'en', or a locale such as 'en-US'.
const en = (language) => String(language || 'de').startsWith('en');

function booleanValues(language) {
  return [
    { value: 'true', label: en(language) ? 'Yes' : 'Ja' },
    { value: 'false', label: en(language) ? 'No' : 'Nein' },
  ];
}

function choiceFor(operator, language) {
  return {
    jl: operator.jl,
    label: operatorWords(language)[operator.jl] || operator.jl,
    needsValue: operator.needsValue,
  };
}

export function operatorChoicesFor(field, language) {
  if (field.kind === 'segment') {
    // A segment is met or it is not -- there is nothing to compare it
    // against, so the full operator list would be a choice with nothing
    // behind it.
    //
    // Both are storable since task 6: a negated segment becomes
    // { "!": { "segment": "<id>" } } inside the rule's `cases`, which
    // conditions.js resolves through the same registered `segment` operation
    // as the plain form. That is also why writeRules leaves the compact
    // { action, source: 'segment', segmentId } shape behind for a negated
    // rule -- that shape has nowhere to keep an operator, which is exactly
    // what made "nicht erfüllt" unsafe to offer until now.
    return [
      { jl: '==', label: en(language) ? 'met' : 'erfüllt', needsValue: false },
      { jl: '!=', label: en(language) ? 'not met' : 'nicht erfüllt', needsValue: false },
    ];
  }
  return operatorsFor(field.fieldType).map((o) => choiceFor(o, language));
}

/**
 * Whether the row's comparison expects something on the right-hand side.
 * Asked of the OPERATOR alone, never of the field: '!!' ("beantwortet") and
 * '!' need nothing, everything else does, and that is true whatever field the
 * row points at -- including one that has since been deleted, where there is
 * no field left to ask.
 *
 * An operator this table does not know (a hand-written one) needs nothing, so
 * nothing is demanded of a row this model does not edit anyway.
 */
function operatorNeedsValue(row) {
  const operator = OPERATORS.find((o) => o.jl === row.operator);
  return operator ? operator.needsValue : false;
}

/**
 * The comparisons to offer for a row whose field is GONE. Without this the
 * comparison picker stood empty and the value box disappeared, while the
 * sentence right underneath went on reading "ein gelöschtes Feld größer als
 * 10.000 ist" -- the two halves of the panel saying different things about
 * the same row, and the editor with no way to see what is actually stored.
 *
 * The row's own fieldType is the best witness left (conditionModel infers it
 * from the stored literal), and the operator it actually carries is put in
 * front if that list does not hold it, so what is stored is always visible.
 */
export function operatorChoicesForRow(row, language) {
  const choices = operatorsFor(row.fieldType).map((o) => choiceFor(o, language));
  if (row.operator !== '' && !choices.some((c) => c.jl === row.operator)) {
    choices.unshift({
      jl: row.operator,
      label: operatorWords(language)[row.operator] || row.operator,
      needsValue: operatorNeedsValue(row),
    });
  }
  return choices;
}

export function valueChoicesFor(field, language) {
  if (field.kind === 'segment') return null;
  // Which types are yes-no is contextRuleHelpers' answer to give: it exports
  // isBooleanFieldType precisely so the value picker and the coercion that
  // turns 'true' into true can never disagree about the list.
  if (isBooleanFieldType(field.fieldType)) return booleanValues(language);
  return field.options && field.options.length > 0 ? field.options : null;
}

export function rowAfterFieldChange(row, field) {
  const operators = operatorChoicesFor(field);
  const keepsOperator = operators.some((o) => o.jl === row.operator);
  const operator = keepsOperator ? row.operator : operators[0].jl;

  if (field.kind === 'segment') {
    return {
      ...row,
      path: field.path,
      kind: 'segment',
      fieldType: 'segment',
      segmentId: field.path,
      operator,
      value: undefined,
    };
  }

  const choices = valueChoicesFor(field);
  let { value } = row;
  if (choices) {
    // The old value belongs to the old field. Keep it only if the new field
    // can actually have it, otherwise take the first one it offers -- an
    // unmatched value would silently build a condition that never fires.
    if (!choices.some((c) => c.value === value)) value = choices[0].value;
  }

  return {
    ...row,
    path: field.path,
    kind: field.kind,
    fieldType: field.fieldType,
    segmentId: undefined,
    operator,
    value,
  };
}

/**
 * A row that was never finished: the address bar with no parameter name
 * typed, or any row with no path at all. That is something the editor is
 * doing right now, in this session, and saving it would write a comparison
 * against an empty var path -- a condition that is stored, looks set, and
 * never fires.
 *
 * A row whose path points at a field that has since been DELETED is
 * deliberately NOT incomplete, although it cannot fire either. It arrived
 * that way out of storage, it is not the mistake of whoever just opened the
 * panel, and blocking the save on it would leave someone who only wants to
 * flip "Zeige" to "Blende aus" with nothing they can do. It is named where it
 * stands ("Gelöschtes Feld — bitte neu wählen") and goes back out exactly as
 * it came in -- the same deal a hand-written row gets, and for the same
 * reason: what goes in comes out.
 *
 * Whether a field still exists therefore plays no part here, which is why
 * this needs no field list to answer.
 *
 * The MISSING VALUE is the same kind of mistake as the missing path, and was
 * missing from this gate (found in the final review of task 7). A new row on
 * a text, number or address-bar field starts with no value at all, and
 * nothing stopped it from being saved: what got stored was
 * `{"==":[{"var":"form.1.name"}, null]}` -- "gleich nichts", which shows the
 * element to exactly those who answered nothing. And it could not be undone:
 * reading that null back refuses it (isEditableValue), so the row came back
 * as an uneditable hand-written one, for good. The old dialog blocked this
 * (ModalCreateContextItem.vue, canSave), so leaving it open here was a step
 * backwards.
 *
 * An EMPTY TEXT is not that: it is an answer somebody may well want to
 * compare against ("Vorname gleich nichts eingetragen"). Only a value that
 * was never given at all counts.
 */
export function isRowIncomplete(row) {
  if (row.raw !== undefined) return false;
  // A segment row is identified by its segment, never by a typed path, and
  // has no value to give.
  if (row.kind === 'segment') return (row.segmentId || '').trim() === '';
  if (row.path.trim() === '') return true;
  return operatorNeedsValue(row) && row.value === undefined;
}

// ---------------------------------------------------------------------------
// Joining and removing rows. Pure on purpose: they mutate the rule they are
// handed and nothing else, so the panel can call them straight from a click
// handler and the tests can call them without mounting anything.
// ---------------------------------------------------------------------------

let rowSeq = 0;

/**
 * A brand-new row pointing at `field`. rowAfterFieldChange decides the
 * comparison and the value, so a row the editor ADDS is built by exactly the
 * same rules as a row whose field the editor SWAPPED -- one shape, one place
 * to get it wrong.
 *
 * WITHOUT a field the row starts blank: no field, no comparison, no value.
 * editor-v2's condition panel asks for that. It used to start every new row on
 * the first field of the list with that field's first answer already picked,
 * and a panel opened on an element WITHOUT a condition then read like a
 * finished, stored one ("Zeige dieses Element, wenn Ihre Rolle gleich
 * Entscheider:in ist") -- an editor took it for exactly that. A blank row
 * cannot be mistaken for a choice somebody made, and isRowIncomplete holds
 * the save until it is filled in (its path is empty).
 *
 * The blank row's `kind` is 'form' only because the type needs one. It must
 * not be 'urlParameter' -- an address-bar row with an empty path is a CHOSEN
 * field whose name is still missing -- nor 'segment'. isRowWithoutField is
 * the one question to ask of it.
 *
 * The id is a v-for key and nothing else (it is never stored), but it has to
 * be unique against the `row-N` ids conditionModel hands out on read, or Vue
 * would reuse one row's dropdowns for another.
 */
function freshRow(field) {
  rowSeq += 1;
  const blank = {
    id: `panel-${rowSeq}`, path: '', operator: '', kind: 'form', fieldType: '',
  };
  if (!field) return blank;
  return rowAfterFieldChange({ ...blank, kind: field.kind, fieldType: field.fieldType }, field);
}

/**
 * A row nobody has pointed at a field yet -- see freshRow. An address-bar row
 * with no name typed is NOT one (its field is chosen, the name is missing),
 * nor is a segment row, nor a hand-written one.
 */
export function isRowWithoutField(row) {
  if (row.raw !== undefined) return false;
  if (row.kind === 'urlParameter' || row.kind === 'segment') return false;
  return row.path.trim() === '';
}

/**
 * A rule this model could not take apart is written back verbatim from its
 * `raw` -- writeRules never looks at its `groups` at all. A row added to such
 * a rule would show in the panel, read back in the sentence, and then be
 * dropped on save without a word: the exact silent loss rule 1 of
 * conditionModel exists to prevent, only in the other direction. The panel
 * hides the joiners on a locked rule; this is the second lock, so that no
 * later caller can reach past it.
 */
function isLocked(rule) {
  return rule.raw !== undefined;
}

/**
 * Another row in the same AND-group: "und".
 *
 * `groupIndex` may point nowhere: removeRow drops a group once its last row
 * goes, and a rule whose last row was removed keeps an empty group list until
 * the editor either adds another row or removes the whole rule. Appending to
 * rule.groups[-1] would throw and take the panel down with it.
 */
export function addRow(rule, groupIndex, field) {
  if (isLocked(rule)) return;
  const target = rule.groups[groupIndex];
  if (!target) {
    rule.groups.push([freshRow(field)]);
    return;
  }
  target.push(freshRow(field));
}

/** An alternative: a second OR-group, read as "oder". */
export function addGroup(rule, field) {
  if (isLocked(rule)) return;
  rule.groups.push([freshRow(field)]);
}

/**
 * One row out again. A group with no rows left goes with it: writeRules
 * filters empty groups out, so one left standing would be a visible "oder"
 * with nothing behind it that then vanishes on save. A rule whose last row of
 * all goes keeps an empty group list -- the panel still shows it, with its
 * joiners, so the editor can build it up again or remove it outright.
 */
export function removeRow(rule, groupIndex, rowIndex) {
  const group = rule.groups[groupIndex];
  if (!group) return;
  group.splice(rowIndex, 1);
  if (group.length === 0) rule.groups.splice(groupIndex, 1);
}
